<script setup lang="ts">
  import { ref } from 'vue'

  import { checkRole } from '@/helpers'

  import { storeToRefs } from 'pinia'
  import { useAuthStore } from '@/stores/auth'

  import { MANAGER_SIDEBAR_MENU } from '@/constants'

  import ManagerSidebar from '@/components/Modals/Manager/ManagerSidebar.vue'
  import ManagerIcon from '@/components/Global/Manager/ManagerIcon.vue'
  import ManagerTextButton from '@/components/Global/Manager/Forms/ManagerTextButton.vue'

  const DEFAULT_CLASSES = 'opacity-0 h-0 py-0'

  const authStore = useAuthStore()
  const { currentRole } = storeToRefs(authStore)

  const menu = MANAGER_SIDEBAR_MENU

  const visible = ref<Record<string | number, boolean>>({})

  const bodyClasses = ref<Record<number, string>>({})

  function onAfterEnter(index: number) {
    bodyClasses.value[index] = 'opacity-100 h-auto py-2'
  }
  function onAfterLeave(index: number) {
    bodyClasses.value[index] = DEFAULT_CLASSES
  }

  const active = ref(false)
  function show() {
    active.value = true
  }
  defineExpose({ show })
</script>

<template>
  <ManagerSidebar
    v-model:active="active"
    sidebar-classes="w-[472px]"
    sidebar-body-classes="!h-full py-10 px-14"
  >
    <template #left>
      <div class="h-full max-h-full border-r border-mbrd">
        <ManagerTextButton
          class="h-14 w-[72px] justify-center"
          @click="active = false"
        >
          <ManagerIcon icon="close-large" color="mblack1" />
        </ManagerTextButton>
      </div>
    </template>

    <template v-for="(item, index) in menu" :key="`menu-item-${index}`">
      <div v-if="checkRole(currentRole, item.roles)" class="mb-4">
        <router-link
          v-if="item.link"
          :to="item.link"
          class="block py-2 hover:opacity-75"
          @click="active = false"
        >
          {{ item.title }}
        </router-link>

        <template v-else>
          <button
            class="flex w-full h-auto py-2 items-center hover:opacity-75"
            @click="visible[index] = !visible[index]"
          >
            <span>
              {{ item.title }}
            </span>

            <span class="ml-[10px]">
              <span
                class="transition-all transform inline-block rotate-180 ease-in-out duration-300"
                :class="{ 'rotate-0': visible[index] }"
              >
                <ManagerIcon icon="arrow-up" color="mgray2" />
              </span>
            </span>
          </button>

          <transition-group
            name="sidebar-menu-transition"
            enter-active-class="transition-all duration-200 ease-out overflow-y-hidden"
            leave-active-class="transition-all duration-100 ease-in overflow-y-hidden"
            enter-to-class="opacity-100 h-auto py-2"
            leave-from-class="opacity-100 h-auto py-2"
            enter-from-class="opacity-0 h-0 py-0"
            leave-to-class="opacity-0 h-0 py-0"
            @after-enter="onAfterEnter(index)"
            @after-leave="onAfterLeave(index)"
          >
            <div
              v-if="item.children?.length && visible[index]"
              :class="bodyClasses[index] ?? DEFAULT_CLASSES"
            >
              <template
                v-for="(childItem, childIndex) in item.children"
                :key="`menu-item-child-${childIndex}`"
              >
                <router-link
                  v-if="checkRole(currentRole, childItem.roles)"
                  :to="childItem.link"
                  class="block py-[6px] text-sm text-mgray hover:opacity-75"
                  @click="active = false"
                >
                  {{ childItem.title }}
                </router-link>
              </template>
            </div>
          </transition-group>
        </template>
      </div>
    </template>
  </ManagerSidebar>
</template>
